import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const links = [
  { slug: "front-of-house", label: "Platform" },
  { slug: "comparison", label: "Solutions" },
  { slug: "pricing", label: "Pricing" },
  { slug: "faq", label: "Resources" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = (slug) => {
    const el = document.getElementById(slug);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goTo = (slug) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(slug), 400);
    } else {
      scrollToId(slug);
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-paper/85 backdrop-blur-md border-b border-verlo/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => {
            if (location.pathname !== "/") navigate("/");
            else if (window.__lenis) window.__lenis.scrollTo(0);
          }}
          className="leading-none"
          aria-label="Verlo home"
        >
          <img src="/verlo-logo.svg" alt="Verlo" className="h-5 md:h-6 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((c) => (
            <button
              key={c.slug}
              data-testid={`nav-link-${c.slug}`}
              onClick={() => goTo(c.slug)}
              className="text-sm text-verlo/60 hover:text-verlo transition-colors duration-300"
            >
              {c.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            data-testid="nav-book-demo"
            href="mailto:hello@verlohq.com"
            className="hidden md:inline text-sm text-verlo/60 hover:text-verlo transition-colors"
          >
            Book a demo
          </a>
          <button
            data-testid="try-verlo-primary"
            onClick={() => navigate("/sandbox")}
            className="hidden sm:inline-flex items-center bg-brass text-paper text-sm font-medium px-5 py-2.5 rounded-full hover:bg-verlo transition-colors duration-300"
          >
            Try Verlo
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-verlo text-sm border border-verlo/20 rounded-full px-4 py-2"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-paper border-t border-verlo/10 px-6 py-6 flex flex-col gap-4">
          {links.map((c) => (
            <button
              key={c.slug}
              data-testid={`nav-link-mobile-${c.slug}`}
              onClick={() => goTo(c.slug)}
              className="text-left text-lg font-display text-verlo"
            >
              {c.label}
            </button>
          ))}
          <a
            href="mailto:hello@verlohq.com"
            className="text-left text-lg font-display text-verlo"
          >
            Book a demo
          </a>
          <button
            data-testid="try-verlo-mobile"
            onClick={() => {
              setOpen(false);
              navigate("/sandbox");
            }}
            className="mt-2 bg-brass text-paper text-sm font-medium px-5 py-3 rounded-full"
          >
            Try Verlo
          </button>
        </div>
      )}
    </header>
  );
}
